import { useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, IconButton, Svg, Typography } from '@altermaind/web/ui';
import { getUserProfileCall, putActivateContextCall } from '@/core/state/api';
import { Roles } from '../../constants';
import { PLATFORM_TYPE_FROM_ROLE, PlatformType } from '../../constants/platformType';
import { usePlatform } from '../../hooks';
import { Badge } from '../Badge';

type Props = {
  goBack?: () => void;
};

export const SwitchPlatform = ({ goBack }: Props) => {
  const t = useTranslations('general.switchPlatform');
  const [onMouseHover, setOnMouseHover] = useState<Roles | undefined>(undefined);
  const { userRole } = getUserProfileCall.hook();
  const { isAdmin, isLearning } = usePlatform();

  const { execute: executePutActivateContext } = putActivateContextCall.hook();

  const changePlatform = (platform: PlatformType) => {
    if (PLATFORM_TYPE_FROM_ROLE[userRole!] !== platform) {
      executePutActivateContext({
        activeContext: platform,
        successSaga: () => {
          window.location.href = '/';
        },
      });
    }
  };

  return (
    <Box
      bg='grey/dark/background'
      p='s'
      display='flex'
      flexDirection='column'
      borderRadius='l'
      boxShadow='0px 0px 16px 0px #00000066'
      minWidth={300}
      justifyContent='center'
      alignItems='center'
    >
      <Typography variant='p1' mt='xxs' textAlign='center' mb='s'>
        {t('title')}
      </Typography>
      <Box
        p='xxs'
        display='flex'
        alignItems='center'
        alignContent='center'
        borderRadius='l'
        style={{ cursor: 'pointer' }}
        flex={1}
        width='100%'
        onClick={() => changePlatform(PlatformType.ADMIN)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(onMouseHover === Roles.Admin ? { backgroundColor: 'grey/dark' } : {})}
        onMouseEnter={() => setOnMouseHover(Roles.Admin)}
        onMouseLeave={() => setOnMouseHover(undefined)}
      >
        <Svg name='world' size='xs' />
        <Typography ml='xxs' variant='p2/light' fontSize='xs' flex={1}>
          {t(`${PlatformType.ADMIN}`)}
        </Typography>
        {isAdmin && (
          <Badge
            label={t('nowLogged')}
            textProps={{ fontSize: 'xxxs', color: 'primary/text' }}
            ml='xs'
            py='none'
            px='xxs'
            opacity={0.9}
          />
        )}
      </Box>
      <Box height={1} backgroundColor='grey/light/background' width='100%' my='xxs' />
      <Box
        p='xxs'
        display='flex'
        alignItems='center'
        alignContent='center'
        borderRadius='l'
        style={{
          cursor: 'pointer',
        }}
        flex={1}
        width='100%'
        onClick={() => changePlatform(PlatformType.LEARNING)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(onMouseHover === Roles.Employee ? { backgroundColor: 'grey/dark' } : {})}
        onMouseEnter={() => setOnMouseHover(Roles.Employee)}
        onMouseLeave={() => setOnMouseHover(undefined)}
      >
        <Svg name='book' size='xs' />
        <Typography ml='xxs' variant='p2/light' fontSize='xs' flex={1}>
          {t(`${PlatformType.LEARNING}`)}
        </Typography>
        {isLearning && (
          <Badge
            label={t('nowLogged')}
            textProps={{ fontSize: 'xxxs', color: 'primary/text' }}
            ml='xs'
            py='none'
            px='xxs'
            opacity={0.9}
          />
        )}
      </Box>
      <Box display='flex' flexDirection='row' alignItems='center' mt='m'>
        <IconButton
          prefixSvg='back'
          variant='link/primary'
          onClick={goBack}
          style={{ textDecoration: 'none', cursor: 'pointer' }}
          svgStyle={{ width: 'xs' }}
          textStyle={{ variant: 'p2/light', marginLeft: 'xxxxs' }}
        >
          <Typography fontWeight='500' ml='xxs' as='span' data-testid='logoutCta'>
            {t('buttonGoBack')}
          </Typography>
        </IconButton>
      </Box>
    </Box>
  );
};
